import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../../components/LoadingScreen';

const ProductCreatePage = () => {
  const { loading } = useAuth();
  const navigate = useNavigate();
  
  const [selectedStage, setSelectedStage] = useState('Discovery');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedClass, setSelectedClass] = useState('Class_1');
  const [step, setStep] = useState('category'); // 'category', 'stage', or 'device_class'

  // Define product category options
  const productCategories = [
    {
      id: 'Therapeutics',
      name: 'Therapeutics',
      description: 'Drug products for treating diseases'
    },
    {
      id: 'Medical_Device',
      name: 'Medical Device',
      description: 'Instruments, apparatus, or machine used in treatment or diagnosis'
    },
    {
      id: 'Diagnostics',
      name: 'Diagnostics',
      description: 'Tests used to detect, diagnose, and monitor diseases'
    },
    {
      id: 'Digital_Health',
      name: 'Digital Health',
      description: 'Software, apps, and digital platforms for healthcare'
    },
    {
      id: 'Vaccines',
      name: 'Vaccines',
      description: 'Biological preparations for immunization against diseases'
    }
  ];

  // Define medical device class options
  const deviceClasses = [
    {
      id: 'Class_1',
      name: 'Class 1',
      description: 'Low-risk devices with simple designs and minimal potential for harm',
      implemented: true
    },
    {
      id: 'Class_2',
      name: 'Class 2',
      description: 'Medium-risk devices that require greater regulatory controls',
      implemented: true
    },
    {
      id: 'Class_3',
      name: 'Class 3',
      description: 'High-risk devices that sustain or support life, are implanted, or present potential risk',
      implemented: true
    }
  ];

  // Define development stage options
  const developmentStages = [
    {
      id: 'Discovery',
      name: 'Discovery',
      description: 'Early concept identification and validation',
      implemented: true
    },
    {
      id: 'Preclinical',
      name: 'Preclinical',
      description: 'Testing in laboratory and animal studies',
      implemented: true
    },
    {
      id: 'Phase_I',
      name: 'Phase I',
      description: 'Initial human testing for safety',
      implemented: true
    },
    {
      id: 'Phase_II',
      name: 'Phase II',
      description: 'Testing for efficacy and side effects',
      implemented: true
    },
    {
      id: 'Phase_III',
      name: 'Phase III',
      description: 'Large scale testing for efficacy and monitoring adverse reactions',
      implemented: true
    },
    {
      id: 'FDA_Review',
      name: 'FDA Review',
      description: 'Regulatory review and approval process',
      implemented: true
    },
    {
      id: 'Approved',
      name: 'Approved',
      description: 'Product approved for market',
      implemented: true
    }
  ];

  // Handle proceeding to the selected stage's form
  const handleProceed = () => {
    if (step === 'category') {
      if (selectedCategory === 'Therapeutics' || selectedCategory === 'Vaccines') {
        // Move to development stage selection for Therapeutics and Vaccines
        setStep('stage');
      } else if (selectedCategory === 'Medical_Device' || selectedCategory === 'Diagnostics' || selectedCategory === 'Digital_Health') {
        // Move to device class selection for Medical Device, Diagnostics, and Digital Health
        setStep('device_class');
      } else {
        // Go to placeholder for other product categories
        navigate(`/products/category/${selectedCategory.toLowerCase()}/input`);
      }
    } else if (step === 'stage') {
      // Format the stage for the URL
      let formattedStage;
      switch (selectedStage) {
        case 'Discovery':
          formattedStage = 'discovery';
          break;
        case 'Preclinical':
          formattedStage = 'preclinical';
          break;
        case 'Phase_I':
          formattedStage = 'phase_i';
          break;
        case 'Phase_II':
          formattedStage = 'phase_ii';
          break;
        case 'Phase_III':
          formattedStage = 'phase_iii';
          break;
        case 'FDA_Review':
          formattedStage = 'fda_review';
          break;
        case 'Approved':
          formattedStage = 'approved';
          break;
        default:
          formattedStage = selectedStage.toLowerCase();
      }
      navigate(`/products/${formattedStage}/input`);
    } else if (step === 'device_class') {
      // Format the device class for the URL
      const formattedClass = selectedClass.toLowerCase();
      navigate(`/products/device/${formattedClass}/input`);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/products" className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Products
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Create New Product</h1>
      
      {step === 'category' ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Product Category</h2>
            <p className="text-gray-600 mb-4">
              Please select the category of your product to proceed with the appropriate creation flow.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {productCategories.map((category) => (
              <div 
                key={category.id}
                className={`border rounded-lg p-4 cursor-pointer transition duration-200 ${
                  selectedCategory === category.id 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-primary-300'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <h3 className="font-medium text-gray-900">{category.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{category.description}</p>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-secondary mr-4"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProceed}
              className="btn-primary"
              disabled={!selectedCategory}
            >
              Next
            </button>
          </div>
        </div>
      ) : step === 'stage' ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <button 
                onClick={() => setStep('category')} 
                className="text-primary-600 hover:text-primary-700 flex items-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                Back to Category Selection
              </button>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Development Stage for Therapeutics</h2>
            <p className="text-gray-600 mb-4">
              Different development stages require different information. Please select the current development stage of your product to proceed with the appropriate form.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {developmentStages.map((stage) => (
              <div 
                key={stage.id}
                className={`border rounded-lg p-4 cursor-pointer transition duration-200 ${
                  selectedStage === stage.id 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-primary-300'
                } ${!stage.implemented ? 'opacity-50' : ''}`}
                onClick={() => stage.implemented && setSelectedStage(stage.id)}
              >
                <div className="flex justify-between items-start">
                  <h3 className="font-medium text-gray-900">{stage.name}</h3>
                  {!stage.implemented && (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{stage.description}</p>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-secondary mr-4"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProceed}
              className="btn-primary"
            >
              Proceed
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <button 
                onClick={() => setStep('category')} 
                className="text-primary-600 hover:text-primary-700 flex items-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                Back to Category Selection
              </button>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Device Class for Medical Device</h2>
            <p className="text-gray-600 mb-4">
              Different device classes require different regulatory requirements. Please select the appropriate class for your medical device.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {deviceClasses.map((deviceClass) => (
              <div 
                key={deviceClass.id}
                className={`border rounded-lg p-4 cursor-pointer transition duration-200 ${
                  selectedClass === deviceClass.id 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-primary-300'
                } ${!deviceClass.implemented ? 'opacity-50' : ''}`}
                onClick={() => deviceClass.implemented && setSelectedClass(deviceClass.id)}
              >
                <div className="flex justify-between items-start">
                  <h3 className="font-medium text-gray-900">{deviceClass.name}</h3>
                  {!deviceClass.implemented && (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{deviceClass.description}</p>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-secondary mr-4"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProceed}
              className="btn-primary"
            >
              Proceed
            </button>
          </div>
        </div>
      )}
      
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        {step === 'category' ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Product Category Information</h3>
            <p className="text-gray-600 mb-4">
              Different product categories follow different development paths:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              <li><span className="font-medium">Therapeutics</span>: Follows the traditional drug development path through clinical trials</li>
              <li><span className="font-medium">Medical Device</span>: Follows regulatory pathways specific to medical devices</li>
              <li><span className="font-medium">Diagnostics</span>: Focuses on test development and validation</li>
              <li><span className="font-medium">Digital Health</span>: Combines software development with healthcare regulation</li>
              <li><span className="font-medium">Vaccines</span>: Specialized pathway for vaccine development and testing</li>
            </ul>
          </>
        ) : step === 'stage' ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Development Stage Information</h3>
            <p className="text-gray-600 mb-4">
              Each development stage in the biotech product lifecycle requires different types of information:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              <li><span className="font-medium">Discovery</span>: Concept description, validation methods, and indications of use</li>
              <li><span className="font-medium">Preclinical</span>: Study results, toxicology data, and pharmacokinetics</li>
              <li><span className="font-medium">Phase I</span>: Safety data, dosing information, and initial human studies</li>
              <li><span className="font-medium">Phase II</span>: Efficacy data, expanded safety information, and patient outcomes</li>
              <li><span className="font-medium">Phase III</span>: Comprehensive clinical data, comparative studies, and statistical analyses</li>
              <li><span className="font-medium">FDA Review</span>: Regulatory submission details, response to inquiries, and approval timeline</li>
              <li><span className="font-medium">Approved</span>: Market strategy, post-approval studies, and commercial metrics</li>
            </ul>
          </>
        ) : (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Medical Device Class Information</h3>
            <p className="text-gray-600 mb-4">
              Medical devices are categorized into different classes based on their risk level and regulatory requirements:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              <li><span className="font-medium">Class 1</span>: Low-risk devices with simple designs that are subject to general controls</li>
              <li><span className="font-medium">Class 2</span>: Medium-risk devices that require special controls in addition to general controls</li>
              <li><span className="font-medium">Class 3</span>: High-risk devices that sustain or support life, are implanted, or present potential risk</li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCreatePage; 